export type ImageDimensionConstraints = {
  minWidth?: number;
  maxWidth?: number;
  maxHeight?: number;
};

export const DEFAULT_IMAGE_DIMENSION_CONSTRAINTS = {
  minWidth: 64,
  maxWidth: 1200,
  maxHeight: 1600,
} satisfies Required<ImageDimensionConstraints>;

export type NormalizedImageDimensions = {
  width?: number;
  height?: number;
};

export function clampImageWidth(
  width: number | undefined,
  constraints: ImageDimensionConstraints = DEFAULT_IMAGE_DIMENSION_CONSTRAINTS,
) {
  if (typeof width !== 'number' || !Number.isFinite(width) || width <= 0) {
    return undefined;
  }

  const minWidth = constraints.minWidth ?? DEFAULT_IMAGE_DIMENSION_CONSTRAINTS.minWidth;
  const maxWidth = constraints.maxWidth ?? DEFAULT_IMAGE_DIMENSION_CONSTRAINTS.maxWidth;

  return Math.max(minWidth, Math.min(maxWidth, width));
}

export function clampImageHeight(
  height: number | undefined,
  constraints: ImageDimensionConstraints = DEFAULT_IMAGE_DIMENSION_CONSTRAINTS,
) {
  if (typeof height !== 'number' || !Number.isFinite(height) || height <= 0) {
    return undefined;
  }

  const maxHeight = constraints.maxHeight ?? DEFAULT_IMAGE_DIMENSION_CONSTRAINTS.maxHeight;

  return Math.min(maxHeight, Math.max(1, height));
}

export function normalizeImageDimensions(
  width: number | 'inherit' | undefined,
  height: number | 'inherit' | undefined,
  constraints: ImageDimensionConstraints = DEFAULT_IMAGE_DIMENSION_CONSTRAINTS,
): NormalizedImageDimensions {
  if (typeof width !== 'number' && typeof height !== 'number') {
    return {};
  }

  if (typeof width !== 'number') {
    return {
      height: typeof height === 'number' ? clampImageHeight(height, constraints) : undefined,
    };
  }

  if (typeof height !== 'number') {
    return { width: clampImageWidth(width, constraints) };
  }

  const aspectRatio = width / height;
  if (!Number.isFinite(aspectRatio) || aspectRatio <= 0) {
    return {
      width: clampImageWidth(width, constraints),
      height: clampImageHeight(height, constraints),
    };
  }

  let nextWidth = clampImageWidth(width, constraints);
  if (!nextWidth) {
    return { height: clampImageHeight(height, constraints) };
  }

  let nextHeight = Math.round(nextWidth / aspectRatio);
  const maxHeight = constraints.maxHeight ?? DEFAULT_IMAGE_DIMENSION_CONSTRAINTS.maxHeight;
  if (nextHeight > maxHeight) {
    nextHeight = maxHeight;
    nextWidth = clampImageWidth(Math.round(nextHeight * aspectRatio), constraints);
  }

  return {
    width: nextWidth,
    height: clampImageHeight(nextHeight, constraints),
  };
}
